import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, Atom, LineChart, Shield, CheckCircle2 } from 'lucide-react';
import { capabilities, heroImages, innovationAreas } from '../data/mockData';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-block mb-6">
                <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                  Innovation-Led Cosmetic R&D
                </span>
              </div>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light leading-tight mb-6">
                Transform Your Beauty Vision Into{' '}
                <span className="text-brand-teal font-medium">Market-Ready Custom Formulations</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Partner with India's premier cosmetic R&D lab. From custom formulation to regulatory compliance, 
                we accelerate your product journey with scientific precision and manufacturing excellence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white bg-brand-teal hover:opacity-90">

                  Book R&D Consultation
                  <ArrowRight size={18} className="ml-2" />
                </Link>
                <Link
                  to="/rd-capabilities"
                  className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-foreground border-2 border-foreground hover:bg-foreground hover:text-white">

                  Explore Capabilities
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                <div>
                  <p className="text-3xl font-semibold text-brand-teal">10+</p>
                  <p className="text-sm text-muted-foreground mt-1">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-brand-teal">800+</p>
                  <p className="text-sm text-muted-foreground mt-1">Formulations Developed</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-brand-teal">150+</p>
                  <p className="text-sm text-muted-foreground mt-1">Brands We Have Worked</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square lg:aspect-[4/5] overflow-hidden">
                <img
                  src={heroImages.primary}
                  alt="Cosmetic laboratory research"
                  className="w-full h-full object-cover hover-lift" />

              </div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 hidden lg:block">
                <img
                  src={heroImages.secondary}
                  alt="Scientist formulation"
                  className="w-full h-full object-cover shadow-lg" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Core <span className="font-medium text-brand-teal">R&D Capabilities</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive formulation development services designed for ambitious beauty brands
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((capability) => {
              const IconComponent = {
                FlaskConical,
                Atom,
                LineChart,
                Shield
              }[capability.icon];

              return (
                <div
                  key={capability.id}
                  className="bg-background p-8 hover-lift cursor-pointer group">

                  <div className="w-12 h-12 bg-brand-teal/10 flex items-center justify-center mb-6 group-hover:bg-brand-teal transition-colors duration-200">
                    <IconComponent className="text-brand-teal group-hover:text-white transition-colors duration-200" size={24} />
                  </div>
                  <h3 className="text-xl font-medium mb-3">{capability.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {capability.description}
                  </p>
                </div>);

            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/rd-capabilities"
              className="inline-flex items-center text-brand-teal font-semibold hover:gap-3 transition-all">

              View All Capabilities
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Innovation Areas Preview */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Product <span className="font-medium text-brand-teal">Innovation Areas</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Specialized formulation expertise across beauty and personal care categories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {innovationAreas.map((area) =>
            <div key={area.id} className="group cursor-pointer">
                <div className="aspect-square overflow-hidden mb-4">
                  <img
                  src={area.image}
                  alt={area.category}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                </div>
                <h3 className="text-xl font-medium mb-3">{area.category}</h3>
                <ul className="space-y-2">
                  {area.products.slice(0, 3).map((product, idx) =>
                <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={16} className="text-brand-teal mt-0.5 flex-shrink-0" />
                      {product}
                    </li>
                )}
                </ul>
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/innovation"
              className="inline-flex items-center text-brand-teal font-semibold hover:gap-3 transition-all">

              Explore All Categories
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-foreground text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-light mb-6">
              Ready to Develop Your Next Breakthrough Product?
            </h2>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Join innovative brands who trust us with their formulation development. 
              Let's discuss your project requirements and accelerate your time to market.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-foreground bg-white hover:bg-white/90">

                Start Your Formulation
                <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link
                to="/process"
                className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white border-2 border-white hover:bg-white hover:text-foreground">

                See How We Work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;